import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Plus, Trash2, ArrowUp, ArrowDown, ImageUp, Save, Images, Layers,
} from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const absUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const Toggle = ({ checked, onChange, label, testid, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className="inline-flex items-center gap-2 disabled:opacity-50"
  >
    <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${checked ? "bg-[#E8E5D0]" : "bg-white/20"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#3A4230] transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </span>
    <span className="text-xs font-bold text-[#E5E2D0]">{label}</span>
  </button>
);

const ImageSlot = ({ label, url, contain, active, uploading, onUpload, onToggle, onRemove, testPrefix }) => {
  const inputRef = useRef(null);
  return (
    <div className="rounded-xl border border-white/12 bg-black/[0.15] p-3" data-testid={`${testPrefix}-slot`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8A58F]">{label}</span>
        <Toggle checked={active} onChange={onToggle} label="Auf Website anzeigen" testid={`${testPrefix}-active-toggle`} />
      </div>
      <div className={`relative w-full ${contain ? "bg-[#2F3626]" : ""} rounded-lg overflow-hidden border border-white/10 mb-2`} style={{ aspectRatio: "4 / 3" }}>
        {url ? (
          <img src={url} alt={label} className={`w-full h-full ${contain ? "object-contain" : "object-cover"}`} data-testid={`${testPrefix}-preview`} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#7E7C6A] text-xs">Kein Bild</div>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Loader2 className="animate-spin text-[#E8E5D0]" size={22} />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid={`${testPrefix}-file-input`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] rounded-full px-3 py-1.5 disabled:opacity-50"
          data-testid={`${testPrefix}-upload-btn`}
        >
          <ImageUp size={13} /> {url ? "Ersetzen" : "Hochladen"}
        </button>
        {url && (
          <button
            type="button"
            onClick={onRemove}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-white/20 hover:bg-white/10 text-[#E5E2D0] rounded-full px-3 py-1.5 disabled:opacity-50"
            data-testid={`${testPrefix}-remove-btn`}
          >
            <Trash2 size={13} /> Entfernen
          </button>
        )}
      </div>
    </div>
  );
};

const StoryCard = ({ story, index, total, busyKey, onPatch, onUpload, onRemoveImage, onDelete, onMove }) => {
  const [name, setName] = useState(story.customer_name || "");
  const [title, setTitle] = useState(story.title || "");
  const [desc, setDesc] = useState(story.description || "");
  const [dur, setDur] = useState(story.coaching_duration || "");
  const [savingText, setSavingText] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    setName(story.customer_name || "");
    setTitle(story.title || "");
    setDesc(story.description || "");
    setDur(story.coaching_duration || "");
  }, [story.id]);

  const dirty =
    (name || "") !== (story.customer_name || "") ||
    (title || "") !== (story.title || "") ||
    (desc || "") !== (story.description || "") ||
    (dur || "") !== (story.coaching_duration || "");

  const saveText = async () => {
    setSavingText(true);
    await onPatch(story.id, { customer_name: name, title, description: desc, coaching_duration: dur });
    setSavingText(false);
  };

  const isCollage = story.layout === "collage";
  const uploadingBefore = busyKey === `${story.id}:before`;
  const uploadingAfter = busyKey === `${story.id}:after`;
  const uploadingCollage = busyKey === `${story.id}:collage`;

  return (
    <div className="rounded-2xl border border-white/12 bg-[#4E5A3B]/60 p-5" data-testid={`admin-story-${index}`}>
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-[#A8A58F] uppercase tracking-[0.18em]">#{index + 1}</span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}
              className="p-1.5 rounded-lg border border-white/15 hover:bg-white/10 disabled:opacity-30" data-testid={`admin-story-${index}-up`}>
              <ArrowUp size={14} className="text-[#E5E2D0]" />
            </button>
            <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1}
              className="p-1.5 rounded-lg border border-white/15 hover:bg-white/10 disabled:opacity-30" data-testid={`admin-story-${index}-down`}>
              <ArrowDown size={14} className="text-[#E5E2D0]" />
            </button>
          </div>
          <Toggle checked={story.active} onChange={(v) => onPatch(story.id, { active: v })} label="Transformation anzeigen" testid={`admin-story-${index}-active`} />
        </div>
        {confirmDel ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#E5E2D0]">Wirklich löschen?</span>
            <button type="button" onClick={() => onDelete(story.id)}
              className="text-xs font-bold bg-red-500/90 hover:bg-red-500 text-white rounded-full px-3 py-1.5" data-testid={`admin-story-${index}-confirm-delete`}>
              Ja, löschen
            </button>
            <button type="button" onClick={() => setConfirmDel(false)}
              className="text-xs font-bold border border-white/20 hover:bg-white/10 text-[#E5E2D0] rounded-full px-3 py-1.5">
              Abbrechen
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setConfirmDel(true)}
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-red-400/40 text-red-300 hover:bg-red-500/15 rounded-full px-3 py-1.5" data-testid={`admin-story-${index}-delete`}>
            <Trash2 size={13} /> Löschen
          </button>
        )}
      </div>

      {/* Layout selector */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8A58F] mr-1">Darstellung:</span>
        <button type="button" onClick={() => onPatch(story.id, { layout: "pair" })}
          className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border ${!isCollage ? "bg-[#E8E5D0] text-[#3A4230] border-transparent" : "border-white/20 text-[#E5E2D0] hover:bg-white/10"}`}
          data-testid={`admin-story-${index}-layout-pair`}>
          <Images size={13} /> Zwei Einzelbilder
        </button>
        <button type="button" onClick={() => onPatch(story.id, { layout: "collage" })}
          className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border ${isCollage ? "bg-[#E8E5D0] text-[#3A4230] border-transparent" : "border-white/20 text-[#E5E2D0] hover:bg-white/10"}`}
          data-testid={`admin-story-${index}-layout-collage`}>
          <Layers size={13} /> Fertige Collage
        </button>
      </div>

      {/* Images */}
      {isCollage ? (
        <div className="mb-4">
          <ImageSlot label="Vorher / Nachher — Collage (ein Bild)" url={absUrl(story.collage_image)} contain active={story.collage_active}
            uploading={uploadingCollage} testPrefix={`admin-story-${index}-collage`}
            onUpload={(f) => onUpload(story.id, "collage", f)} onToggle={(v) => onPatch(story.id, { collage_active: v })}
            onRemove={() => onRemoveImage(story.id, "collage")} />
          <p className="mt-2 text-[11px] text-[#A8A58F]">Das fertige Bild wird vollständig (ohne Beschneiden) angezeigt, Vorher links / Nachher rechts.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <ImageSlot label="Vorher" url={absUrl(story.before_image)} active={story.before_active}
            uploading={uploadingBefore} testPrefix={`admin-story-${index}-before`}
            onUpload={(f) => onUpload(story.id, "before", f)} onToggle={(v) => onPatch(story.id, { before_active: v })}
            onRemove={() => onRemoveImage(story.id, "before")} />
          <ImageSlot label="Nachher" url={absUrl(story.after_image)} active={story.after_active}
            uploading={uploadingAfter} testPrefix={`admin-story-${index}-after`}
            onUpload={(f) => onUpload(story.id, "after", f)} onToggle={(v) => onPatch(story.id, { after_active: v })}
            onRemove={() => onRemoveImage(story.id, "after")} />
        </div>
      )}

      {/* Text fields */}
      <div className="grid grid-cols-1 gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8A58F] mb-1">Kundenname / Anzeigename (optional)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid={`admin-story-${index}-name`}
              className="w-full rounded-lg bg-black/[0.2] border border-white/15 px-3 py-2 text-sm text-[#F3F1E8] focus:outline-none focus:border-[#E8E5D0]/50" placeholder="z. B. Marco S." />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8A58F] mb-1">Titel (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`admin-story-${index}-title`}
              className="w-full rounded-lg bg-black/[0.2] border border-white/15 px-3 py-2 text-sm text-[#F3F1E8] focus:outline-none focus:border-[#E8E5D0]/50" placeholder="z. B. -40 kg · Muskeln aufgebaut" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8A58F] mb-1">Beschreibung (optional)</label>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} data-testid={`admin-story-${index}-desc`}
            className="w-full rounded-lg bg-black/[0.2] border border-white/15 px-3 py-2 text-sm text-[#F3F1E8] focus:outline-none focus:border-[#E8E5D0]/50 resize-y" placeholder="Kurzes Zitat oder Beschreibung" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8A58F] mb-1">Coaching-Dauer (optional)</label>
          <input value={dur} onChange={(e) => setDur(e.target.value)} data-testid={`admin-story-${index}-duration`}
            className="w-full rounded-lg bg-black/[0.2] border border-white/15 px-3 py-2 text-sm text-[#F3F1E8] focus:outline-none focus:border-[#E8E5D0]/50 sm:max-w-xs" placeholder="z. B. 6 Monate Coaching" />
        </div>
        <div>
          <button type="button" onClick={saveText} disabled={!dirty || savingText} data-testid={`admin-story-${index}-save`}
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] rounded-full px-4 py-2 disabled:opacity-40">
            {savingText ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Texte speichern
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminTransformations = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(
    () => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 30000 }),
    [token]
  );

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const handleAuthErr = useCallback((e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 403 && detail === "PASSWORD_CHANGE_REQUIRED") { navigate("/admin/leads", { replace: true }); return true; }
    if ([401, 403].includes(status)) { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login", { replace: true }); return true; }
    return false;
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stories");
      setStories(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, [api, handleAuthErr]);

  useEffect(() => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    load();
  }, [token, load, navigate]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/stories/${id}`, body);
      await load();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
  };

  const upload = async (id, kind, file) => {
    setBusyKey(`${id}:${kind}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/stories/${id}/image/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bild gespeichert.");
      await load();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setBusyKey(null);
    }
  };

  const removeImage = async (id, kind) => {
    setBusyKey(`${id}:${kind}`);
    try {
      await api.delete(`/admin/stories/${id}/image/${kind}`);
      await load();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Entfernen fehlgeschlagen.");
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/stories/${id}`);
      toast.success("Transformation gelöscht.");
      await load();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Löschen fehlgeschlagen.");
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/stories");
      toast.success("Neue Transformation erstellt.");
      await load();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Erstellen fehlgeschlagen.");
    } finally {
      setCreating(false);
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= stories.length) return;
    const next = [...stories];
    [next[index], next[target]] = [next[target], next[index]];
    setStories(next);
    try {
      await api.post("/admin/stories/reorder", { ids: next.map((s) => s.id) });
      await load();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Reihenfolge fehlgeschlagen.");
      await load();
    }
  };

  return (
    <div className="min-h-screen bg-[#3A4230] text-[#F3F1E8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-4">
          <ArrowLeft size={14} /> Zurück zum Dashboard
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Vorher / Nachher — Transformationen</h1>
            <p className="mt-2 text-sm text-[#C9C6B4] max-w-2xl">
              Verwalte hier alle Resultate der Startseite. Pro Eintrag kannst du eine fertige Collage (ein Bild) oder zwei getrennte
              Bilder nutzen, Bilder ein-/ausblenden, Texte pflegen, die Reihenfolge ändern und Einträge löschen. Nur aktive Einträge
              erscheinen öffentlich.
            </p>
          </div>
          <button type="button" onClick={create} disabled={creating} data-testid="admin-story-create"
            className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider px-5 py-3 rounded-full disabled:opacity-50">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Neue Transformation
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#E8E5D0]" size={28} /></div>
        ) : stories.length === 0 ? (
          <div className="rounded-2xl border border-white/12 bg-[#4E5A3B]/60 p-10 text-center text-[#C9C6B4]" data-testid="admin-stories-empty">
            Noch keine Transformationen. Lege die erste mit „Neue Transformation" an.
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-stories-list">
            {stories.map((s, i) => (
              <StoryCard key={s.id} story={s} index={i} total={stories.length} busyKey={busyKey}
                onPatch={patch} onUpload={upload} onRemoveImage={removeImage} onDelete={remove} onMove={move} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTransformations;
